import { useEffect, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtEur } from "@/lib/reports";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const strip = (l) => ({ product_id: l.product_id || null, description: l.description, quantity: Number(l.quantity) || 1, unit: l.unit || null, unit_price: l.unit_price ?? null });

const Picker = ({ kind, onPick, testId }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/report-catalog", { params: { q } }).then((r) => setRes(r.data.items.filter((p) => p.kind === kind))).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q, kind]);
  const free = () => { if (q.trim()) { onPick({ description: q.trim(), quantity: 1, unit: kind === "servizio" ? "h" : "pz" }); setQ(""); } };
  return (
    <div className="relative">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel catalogo o scrivi una descrizione" className="pl-9 pr-24" data-testid={`${testId}-search`} />
      <Button size="sm" variant="ghost" className="absolute right-1 top-0.5 gap-1" onClick={free} disabled={!q.trim()} data-testid={`${testId}-add-free`}><Plus className="h-4 w-4" />Libero</Button>
      {res.length > 0 && <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-control border border-line bg-white shadow-lg">
        {res.map((p) => <li key={p.id}><button type="button" className="flex w-full justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`${testId}-result-${p.id}`}
          onClick={() => { onPick({ product_id: p.id, description: p.name, quantity: 1, unit: p.unit }); setQ(""); }}>
          <span className="truncate"><span className="font-mono text-xs text-slate-400">{p.code}</span> {p.name}</span>{p.base_price != null && <span className="text-xs text-slate-500">{fmtEur(p.base_price)}</span>}</button></li>)}
      </ul>}
    </div>
  );
};

export const LinesEditor = ({ rep, field, kind, title, onSaved }) => {
  const [lines, setLines] = useState(rep[field] || []);
  const editable = rep.can.edit, priced = rep.can.rate;
  const save = async (next) => {
    setLines(next);
    try { await api.patch(`/service-reports/${rep.id}`, { [field]: next.map(strip) }); onSaved?.(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const upd = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const tid = `report-${field}`;
  return (
    <Panel className="p-5" data-testid={tid}>
      <SectionLabel>{title}</SectionLabel>
      {!lines.length && <p className="mt-2 text-sm text-slate-500">Nessuna voce.</p>}
      <ul className="mt-2 divide-y divide-line">{lines.map((l, i) => (
        <li key={l.id || i} className="flex flex-wrap items-center gap-2 py-2" data-testid={`${tid}-line-${i}`}>
          <span className="min-w-0 flex-1 text-sm text-ink">{l.code && <span className="mr-1 font-mono text-xs text-slate-400">{l.code}</span>}{l.description}</span>
          <Input type="number" min={0} step="any" value={l.quantity} disabled={!editable} onChange={(e) => upd(i, "quantity", e.target.value)} onBlur={() => save(lines)} className="h-9 w-20" data-testid={`${tid}-qty-${i}`} />
          <span className="w-8 text-xs text-slate-500">{l.unit}</span>
          {priced && <Input type="number" min={0} step="0.01" placeholder="Listino" value={l.unit_price ?? ""} onChange={(e) => upd(i, "unit_price", e.target.value === "" ? null : Number(e.target.value))} onBlur={() => save(lines)} className="h-9 w-28" data-testid={`${tid}-price-${i}`} />}
          {editable && <Button size="icon" variant="ghost" onClick={() => save(lines.filter((_, j) => j !== i))} aria-label="Rimuovi" data-testid={`${tid}-remove-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
        </li>))}
      </ul>
      {editable && <div className="mt-3"><Picker kind={kind} testId={tid} onPick={(l) => save([...lines, l])} /></div>}
    </Panel>
  );
};
